import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  ChevronDown,
  Filter,
  Loader2,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  CasaSelector,
  MonedaToggle,
} from '@/shared/components/TipoCambioControls';
import { CASAS_DOLAR_LABEL as CASAS_LABEL } from '@/shared/lib/tipoCambio';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { MovimientosDetalleDialog } from './MovimientosDetalleDialog';
import { ConsumoFlujoDialog } from './ConsumoFlujoDialog';
import { ExportarXlsxButton } from './ExportarXlsxButton';
import { cn } from '@/shared/lib/cn';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  getEstadoFinanciero,
  getEstadoFinancieroDetalle,
  CONSUMO_TOTAL,
  MOSTRAR_CAUCION,
  type EfCaucionDia,
  type EfGranularidad,
  type EfResponse,
  type EfRow,
  type EfPeriodo,
  type EfCasa,
  type EfVista,
} from './api';

/**
 * Estado Financiero — the month-by-month statement, grouped by EF Ítem
 * and SubÍtem, with the two computed lines and a per-cell drill-down.
 *
 * Bespoke report (same pattern as Cierres de Caja / Facturación): it
 * owns its toolbar and body, and the Reportes shell neither previews nor
 * exports it.
 *
 * Layout mirrors the model the client already reads: Ítem column frozen
 * left, one column per month, Total last. Ítem rows carry the "% del
 * Flujo Op." subtitle; the computed lines are tinted so the eye lands on
 * Margen Bruto and G/P Operativo first.
 */

// ---------------------------------------------------------------------------
// Filter state — owned here, rendered into the shell's toolbar row.
// ---------------------------------------------------------------------------

export type EfFilters = ReturnType<typeof useEstadoFinancieroFilters>;

function defaultPeriodo(): EfPeriodo {
  const hoy = new Date();
  const desde = new Date(hoy.getFullYear(), hoy.getMonth() - 11, 1);
  const fmt = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  return {
    desde: fmt(desde),
    hasta: fmt(hoy),
    granularidad: 'mensual',
    vista: 'local',
    casa: 'oficial',
  };
}

/**
 * Encabezado de columna. En diario la clave es ISO (`2025-07-14`) porque
 * así ordena sola; recién acá se da vuelta a `14/07/2025`, que es como lo
 * lee el negocio.
 */
export function tituloColumna(
  clave: string,
  granularidad: EfGranularidad,
): string {
  if (granularidad !== 'diario') return clave;
  const [a, m, d] = clave.split('-');
  return `${d}/${m}/${a}`;
}

export function useEstadoFinancieroFilters(enabled: boolean) {
  const { config: activeCountry } = useActiveCountry();
  const [periodo, setPeriodo] = useState<EfPeriodo>(defaultPeriodo);

  const query = useQuery({
    // Country in the key so AR/MX never share a cache slot.
    queryKey: ['estado-financiero', activeCountry.code, periodo],
    queryFn: () => getEstadoFinanciero(periodo),
    enabled,
    staleTime: 60_000,
  });

  return { periodo, setPeriodo, query };
}

/**
 * Período — Mensual / Diario. Fuera del popover de filtros a propósito:
 * cambia lo que la tabla muestra, no qué datos trae, y se alterna seguido.
 * Dos opciones = segmentado de un clic, igual que el Moneda ARS/USD del
 * modelo original.
 */
function GranularidadToggle({
  valor,
  onChange,
}: {
  valor: EfGranularidad;
  onChange: (g: EfGranularidad) => void;
}) {
  const opciones: ReadonlyArray<{
    id: EfGranularidad;
    label: string;
    title: string;
  }> = [
    { id: 'mensual', label: 'Mensual', title: 'Una columna por mes (MM-AAAA)' },
    {
      id: 'diario',
      label: 'Diario',
      title: 'Una columna por fecha con movimientos',
    },
  ];
  // Fallback explícito: si por lo que sea llega sin valor, la vista es
  // mensual — nunca el segmentado sin nada seleccionado.
  const activo: EfGranularidad = valor === 'diario' ? 'diario' : 'mensual';
  return (
    <div
      className="flex h-12 shrink-0 items-center gap-1"
      role="group"
      aria-label="Período de las columnas"
    >
      {opciones.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={activo === o.id}
          title={o.title}
          className={cn(
            'rounded-md px-3 py-2 text-sm font-medium transition-colors',
            activo === o.id
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function EstadoFinancieroToolbar({
  state,
  exportar,
}: {
  state: EfFilters;
  /** Exportador de la solapa activa. Vive acá y no en cada panel para no
   *  gastar una fila entera de alto en un solo botón. */
  exportar?: () => Promise<void>;
}) {
  const { periodo, setPeriodo, query } = state;
  const { config: activeCountry } = useActiveCountry();
  return (
    <>
      <MonedaToggle
        valor={periodo.vista}
        monedaLocal={activeCountry.code === 'MX' ? 'MXN' : 'ARS'}
        onChange={(vista) => setPeriodo({ ...periodo, vista })}
      />
      <GranularidadToggle
        valor={periodo.granularidad}
        onChange={(granularidad) => setPeriodo({ ...periodo, granularidad })}
      />
      <CasaSelector
        valor={periodo.casa}
        onChange={(casa) => setPeriodo({ ...periodo, casa })}
      />
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-12 gap-2">
            <Filter className="h-4 w-4" aria-hidden />
            {`${periodo.desde} → ${periodo.hasta}`}
            {query.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-4">
          <div className="space-y-3">
            <MesSelector
              label="Desde"
              value={periodo.desde}
              onChange={(desde) => setPeriodo({ ...periodo, desde })}
            />
            <MesSelector
              label="Hasta"
              value={periodo.hasta}
              onChange={(hasta) => setPeriodo({ ...periodo, hasta })}
            />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Movimientos de caja agrupados por Ítem y SubÍtem del Estado
            Financiero. Excluye anulados, aperturas/cierres de caja y
            transferencias entre cuentas propias.
          </p>
        </PopoverContent>
      </Popover>
      {exportar && query.data && (
        <ExportarXlsxButton onExport={exportar} className="h-12" />
      )}
    </>
  );
}

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function MesSelector({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const [mm, yyyy] = value.split('/');
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 1; y >= currentYear - 4; y--)
    years.push(String(y));
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Select value={mm} onValueChange={(m) => onChange(`${m}/${yyyy}`)}>
          <SelectTrigger aria-label={`${label} — mes`}>
            <SelectValue placeholder="Mes" />
          </SelectTrigger>
          <SelectContent>
            {MESES.map((nombre, i) => (
              <SelectItem key={nombre} value={String(i + 1).padStart(2, '0')}>
                {nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={yyyy} onValueChange={(y) => onChange(`${mm}/${y}`)}>
          <SelectTrigger aria-label={`${label} — año`}>
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={y}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

export function EstadoFinancieroBody({ state }: { state: EfFilters }) {
  const { query } = state;
  const [detalle, setDetalle] = useState<{
    row: EfRow;
    mes: string | null;
  } | null>(null);
  const [caucionMes, setCaucionMes] = useState<string | null>(null);
  const [consumo, setConsumo] = useState<string | null>(null);

  const data = query.data;
  const meses = data?.meses ?? [];

  if (query.isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" aria-hidden />
          No se pudo generar el Estado Financiero.
        </div>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="min-h-0 flex-1 space-y-2 rounded-2xl border bg-card p-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card text-sm text-muted-foreground">
        Sin movimientos en el período seleccionado.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <KpiCards
        ingresos={data.kpis.ingresos}
        egresos={data.kpis.egresos}
        gpOperativo={data.kpis.gpOperativo}
        moneda={data.moneda}
      />

      <EfAvisos data={data} />

      <div className="min-h-0 flex-1 overflow-auto rounded-2xl border bg-card">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 border-b bg-card px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Ítem
              </th>
              {meses.map((mes) => (
                <ColumnaHeader
                  key={mes}
                  label={tituloColumna(mes, data.granularidad)}
                  onClick={() => setConsumo(mes)}
                />
              ))}
              <ColumnaHeader
                label="Total"
                onClick={() => setConsumo(CONSUMO_TOTAL)}
              />
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <StatementRow
                key={`${row.kind}-${row.label}-${i}`}
                row={row}
                meses={meses}
                moneda={data.moneda}
                flujoOperativoPorMes={data.flujoOperativoPorMes}
                flujoOperativoTotal={data.kpis.ingresos}
                onDrill={(mes) => setDetalle({ row, mes })}
              />
            ))}
            {/* Sin caución en la vista USD: la serie de tasas es en pesos. */}
            {MOSTRAR_CAUCION && data.caucion && (
              <CaucionRow
                meses={meses}
                caucion={data.caucion}
                moneda={data.moneda}
                onDrill={setCaucionMes}
              />
            )}
          </tbody>
        </table>
      </div>

      <ConsumoFlujoDialog
        titulo={
          consumo === CONSUMO_TOTAL
            ? 'Total del período'
            : consumo
              ? tituloColumna(consumo, data.granularidad)
              : null
        }
        periodo={consumo ? data.consumo[consumo] : undefined}
        moneda={data.moneda}
        onClose={() => setConsumo(null)}
      />

      <CaucionDialog
        mes={caucionMes}
        dias={caucionMes ? (data.caucion?.detallePorMes[caucionMes] ?? []) : []}
        moneda={data.moneda}
        granularidad={data.granularidad}
        onClose={() => setCaucionMes(null)}
      />

      <DetalleDialog
        detalle={detalle}
        moneda={data.moneda}
        granularidad={data.granularidad}
        vista={data.vista}
        casa={data.casa}
        onClose={() => setDetalle(null)}
      />
    </div>
  );
}

/**
 * Los avisos que califican los números: qué está estimado, qué quedó afuera,
 * qué todavía puede cambiar. Los comparten el Estado Financiero y Flujo por
 * mes porque las dos solapas publican las MISMAS cifras — si sólo una
 * avisara, la otra se leería como definitiva.
 */
export function EfAvisos({ data }: { data: EfResponse }) {
  const avisos: Array<{ id: string; tono: Tono; cuerpo: ReactNode }> = [];

  // Con qué serie se armaron estos números. Sin esto, dos personas mirando
  // el mismo mes con distinta casa concluyen que el tablero está roto.
  avisos.push({
    id: 'cotizacion',
    tono: 'neutro',
    cuerpo:
      data.vista === 'usd' ? (
        <>
          <strong>Todos los movimientos</strong>, expresados en dólares al{' '}
          <strong>{CASAS_LABEL[data.casa]} de la fecha de cada uno</strong>. Los
          que ya están en cajas en dólares van con su importe original. La
          caución no aplica: su serie de tasas es en pesos.
        </>
      ) : (
        <>
          Los movimientos en cajas en dólares se convierten con el{' '}
          <strong>{CASAS_LABEL[data.casa]} de la fecha de cada uno</strong>, no
          con el tipo de cambio que se haya cargado en Tesorería: el estado
          valúa todo con la misma vara.
        </>
      ),
  });

  // Vale en las dos vistas: en pesos son los dólares que no se pudieron
  // convertir; en dólares, los pesos de un país sin serie de cotización.
  if (data.sinCotizacion.movimientos > 0) {
    avisos.push({
      id: 'sin-cotizacion',
      tono: 'alerta',
      cuerpo: (
        <>
          {data.sinCotizacion.movimientos.toLocaleString('es-AR')} movimiento
          {data.sinCotizacion.movimientos === 1 ? '' : 's'} sin rueda de{' '}
          {CASAS_LABEL[data.casa]} para su fecha — quedan{' '}
          <strong>fuera</strong> del estado.
        </>
      ),
    });
  }

  // Provisorio: los movimientos sin cerrar YA están sumados en la tabla
  // (sólo excluimos anulados). Sin este aviso, un mes que todavía puede
  // moverse se lee como definitivo.
  if (data.sinConfirmar.movimientos > 0) {
    avisos.push({
      id: 'sin-confirmar',
      tono: 'alerta',
      cuerpo: (
        <>
          {data.sinConfirmar.movimientos.toLocaleString('es-AR')} movimiento
          {data.sinConfirmar.movimientos === 1 ? '' : 's'} de{' '}
          {data.sinConfirmar.meses
            .map((m) => tituloColumna(m, data.granularidad))
            .join(', ')}{' '}
          <strong>todavía no pasaron por Cierre de Caja</strong> (
          {formatMoney(data.sinConfirmar.monto, data.moneda)} entre ingresos y
          egresos). Están incluidos en el estado, así que esos períodos son{' '}
          <strong>provisorios</strong> hasta que se cierren.
        </>
      ),
    });
  }

  // La serie de caución es una función escalón: sin rueda nueva, la última
  // tasa se arrastra sin límite. Con la serie vencida el número sigue
  // creciendo y nada lo delata.
  if (MOSTRAR_CAUCION && data.caucion && data.caucion.extrapolado.meses > 0) {
    avisos.push({
      id: 'caucion-vencida',
      tono: 'alerta',
      cuerpo: (
        <>
          La serie de tasas de caución termina el{' '}
          <strong>{fechaCortaIso(data.caucion.ultimaTasa)}</strong>.{' '}
          {data.caucion.extrapolado.meses} período
          {data.caucion.extrapolado.meses === 1 ? '' : 's'} del filtro se
          devengaron arrastrando esa última tasa —{' '}
          <strong>
            {formatMoney(data.caucion.extrapolado.monto, data.moneda)}
          </strong>{' '}
          de la caución que se muestra es estimada. Cargá la serie actualizada
          en Configuración → Tasas de Caución.
        </>
      ),
    });
  }

  if (data.sinClasificar.movimientos > 0) {
    avisos.push({
      id: 'sin-clasificar',
      tono: 'alerta',
      cuerpo: (
        <>
          {data.sinClasificar.movimientos.toLocaleString('es-AR')} movimiento
          {data.sinClasificar.movimientos === 1 ? '' : 's'} sin SubÍtem de EF (
          {formatMoney(data.sinClasificar.monto, data.moneda)}) — figuran en la
          fila <strong>Sin clasificar</strong>, que se puede abrir para ver de
          qué categoría y cuenta contable vienen. Se resuelven asignándole un
          SubÍtem a esa cuenta en Configuración; si el par todavía no existe en
          el plan de cuentas, primero hay que crear la cuenta.
        </>
      ),
    });
  }

  if (avisos.length === 0) return null;

  const alertas = avisos.filter((a) => a.tono === 'alerta').length;

  // `<details>` nativo: sin estado propio, plegable con teclado y accesible
  // sin nada extra. Arranca cerrado — eran cinco cajas que empujaban la tabla
  // fuera de pantalla — pero el resumen queda SIEMPRE visible, así que nunca
  // se publican números calificados sin decirlo.
  return (
    <details className="group shrink-0 rounded-lg border bg-card">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2 text-xs text-muted-foreground marker:content-none hover:bg-muted/40">
        <AlertTriangle
          className={cn('h-3.5 w-3.5 shrink-0', alertas > 0 ? 'text-amber-600' : 'text-sky-600')}
          aria-hidden
        />
        <span>
          {avisos.length} aviso{avisos.length === 1 ? '' : 's'} sobre estos
          números
          {alertas > 0 && (
            <span className="text-amber-700">
              {' '}
              · {alertas} requiere{alertas === 1 ? '' : 'n'} atención
            </span>
          )}
        </span>
        <ChevronDown
          className="ml-auto h-4 w-4 shrink-0 transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="flex flex-col gap-2 border-t p-3">
        {avisos.map((a) => (
          <div key={a.id} className={cn('rounded-lg border px-4 py-2 text-xs', TONO[a.tono])}>
            {a.cuerpo}
          </div>
        ))}
      </div>
    </details>
  );
}

type Tono = 'neutro' | 'info' | 'alerta';

const TONO: Record<Tono, string> = {
  neutro: 'bg-muted/40 text-muted-foreground',
  info: 'border-sky-300 bg-sky-50 text-sky-900',
  alerta: 'border-amber-300 bg-amber-50 text-amber-900',
};

/**
 * Encabezado de columna. Clickeable: abre el gráfico de consumo del período,
 * igual que en el modelo. El ícono lo delata — sin él nadie prueba.
 */
function ColumnaHeader({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <th className="whitespace-nowrap border-b bg-card px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <button
        type="button"
        onClick={onClick}
        title="Ver consumo del flujo operativo"
        className="inline-flex items-center gap-1.5 rounded transition-colors hover:text-foreground"
      >
        {label}
        <BarChart3 className="h-3.5 w-3.5 opacity-60" aria-hidden />
      </button>
    </th>
  );
}

function StatementRow({
  row,
  meses,
  moneda,
  flujoOperativoPorMes,
  flujoOperativoTotal,
  onDrill,
}: {
  row: EfRow;
  meses: string[];
  moneda: string;
  flujoOperativoPorMes: Record<string, number>;
  flujoOperativoTotal: number;
  onDrill: (mes: string | null) => void;
}) {
  const isItem = row.kind === 'item';
  const isComputed = row.kind === 'computed';
  // Only SubÍtem rows drill down — an Ítem cell is a roll-up of several
  // SubÍtems and a computed line is arithmetic, not movements.
  const drillable = row.kind === 'subitem';

  // "% del Flujo Op." — the share subtitle the client reads on every
  // Ítem and computed line. Undefined (rendered blank) when the month has
  // no operating inflow, rather than a misleading 0%.
  const pct = (mes: string | null) => {
    const base =
      mes === null ? flujoOperativoTotal : (flujoOperativoPorMes[mes] ?? 0);
    if (!base) return null;
    const valor = mes === null ? row.total : (row.porMes[mes] ?? 0);
    return `${Math.abs((valor / base) * 100)
      .toFixed(1)
      .replace('.', ',')}% del Flujo Op.`;
  };

  const showPct = isItem || isComputed;

  return (
    <tr
      className={cn(
        'border-b',
        isItem && 'bg-slate-100/80 font-semibold',
        isComputed && 'bg-amber-50 font-semibold',
        !isItem && !isComputed && 'hover:bg-primary/5',
      )}
    >
      <th
        scope="row"
        className={cn(
          'sticky left-0 z-10 max-w-[22rem] truncate border-b px-4 py-2 text-left font-normal',
          isItem && 'bg-slate-100/95 font-semibold uppercase tracking-wide',
          isComputed && 'bg-amber-50 font-semibold',
          !isItem && !isComputed && 'bg-card pl-8 text-muted-foreground',
        )}
        title={row.label}
      >
        {!isItem && !isComputed && <span aria-hidden>· </span>}
        {row.label}
      </th>
      {meses.map((mes) => (
        <Cell
          key={mes}
          valor={row.porMes[mes]}
          moneda={moneda}
          subtitle={showPct ? pct(mes) : null}
          onClick={drillable ? () => onDrill(mes) : undefined}
        />
      ))}
      <Cell
        valor={row.total}
        moneda={moneda}
        subtitle={showPct ? pct(null) : null}
        onClick={drillable ? () => onDrill(null) : undefined}
        emphasis
      />
    </tr>
  );
}

function Cell({
  valor,
  moneda,
  subtitle,
  onClick,
  emphasis,
}: {
  valor: number | undefined;
  moneda: string;
  subtitle: string | null;
  onClick?: () => void;
  emphasis?: boolean;
}) {
  const vacio = valor === undefined || valor === 0;
  return (
    <td
      className={cn(
        'whitespace-nowrap border-b px-4 py-2 text-right tabular-nums',
        emphasis && 'font-semibold',
        onClick && !vacio && 'cursor-pointer hover:underline',
      )}
      onClick={vacio ? undefined : onClick}
      title={onClick && !vacio ? 'Ver movimientos' : undefined}
    >
      {vacio ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className={valor! < 0 ? 'text-red-600' : 'text-emerald-700'}>
          {formatMoney(valor!, moneda)}
        </span>
      )}
      {subtitle && (
        <span className="block text-[10px] font-normal text-muted-foreground">
          {subtitle}
        </span>
      )}
    </td>
  );
}

/**
 * Caución row — interest the operating cash would have earned placed at
 * the daily rate. Sits below the statement because it is a simulation,
 * not a movement: nothing in it comes from the ledger.
 */
function CaucionRow({
  meses,
  caucion,
  moneda,
  onDrill,
}: {
  meses: string[];
  caucion: {
    pct: number;
    desde: string;
    porMes: Record<string, number>;
    tasasCargadas: number;
  };
  moneda: string;
  onDrill: (mes: string) => void;
}) {
  const total = meses.reduce((acc, m) => acc + (caucion.porMes[m] ?? 0), 0);
  const sinTasas = caucion.tasasCargadas === 0;
  return (
    <tr className="border-b bg-red-50/60 font-semibold">
      <th
        scope="row"
        className="sticky left-0 z-10 border-b bg-red-50 px-4 py-2 text-left text-red-800"
        title={
          sinTasas
            ? 'No hay tasas de caución cargadas — se muestra 0. Cargalas en Configuración.'
            : `${caucion.pct}% del G/P acumulado desde ${caucion.desde}, a la tasa diaria de la serie.`
        }
      >
        {`Caución (${caucion.pct}% cash, ${moneda})`}
      </th>
      {meses.map((mes) => {
        const valor = caucion.porMes[mes] ?? 0;
        return (
          <td
            key={mes}
            className={cn(
              'whitespace-nowrap border-b px-4 py-2 text-right tabular-nums',
              valor > 0 && 'cursor-pointer hover:underline',
            )}
            onClick={valor > 0 ? () => onDrill(mes) : undefined}
            title={valor > 0 ? 'Ver marcha de cálculo' : undefined}
          >
            {valor > 0 ? (
              <span className="text-emerald-700">
                {formatMoney(valor, moneda)}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </td>
        );
      })}
      <td className="whitespace-nowrap border-b px-4 py-2 text-right tabular-nums text-emerald-700">
        {formatMoney(total, moneda)}
      </td>
    </tr>
  );
}

/** "Marcha de cálculo" — the day-by-day accrual behind one month. */
function CaucionDialog({
  mes,
  dias,
  moneda,
  granularidad,
  onClose,
}: {
  mes: string | null;
  dias: EfCaucionDia[];
  moneda: string;
  granularidad: EfGranularidad;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!mes} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {`Marcha de cálculo — Caución (${moneda}) · ${
              mes ? tituloColumna(mes, granularidad) : ''
            }`}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3 text-right">G/P acum</th>
                <th className="py-2 pr-3 text-right">Int T-1</th>
                <th className="py-2 pr-3 text-right">Base</th>
                <th className="py-2 pr-3 text-right">Tasa</th>
                <th className="py-2 text-right">Int T</th>
              </tr>
            </thead>
            <tbody>
              {dias.map((d) => (
                <tr key={d.fecha} className="border-b last:border-b-0">
                  <td className="whitespace-nowrap py-2 pr-3">{d.fecha}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {formatMoney(d.gpAcum, moneda)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {formatMoney(d.intAnterior, moneda)}
                  </td>
                  <td
                    className={cn(
                      'py-2 pr-3 text-right tabular-nums',
                      d.base < 0 && 'text-red-600',
                    )}
                  >
                    {formatMoney(d.base, moneda)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {`${(d.tasa * 100).toFixed(4).replace('.', ',')}%`}
                  </td>
                  <td className="py-2 text-right tabular-nums text-emerald-700">
                    {formatMoney(d.intDia, moneda)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KpiCards({
  ingresos,
  egresos,
  gpOperativo,
  moneda,
}: {
  ingresos: number;
  egresos: number;
  gpOperativo: number;
  moneda: string;
}) {
  const cards = [
    { label: 'Ingresos totales', valor: ingresos, tone: 'text-emerald-700' },
    { label: 'Egresos totales', valor: egresos, tone: 'text-red-600' },
    {
      label: 'G/P operativo',
      valor: gpOperativo,
      tone: gpOperativo < 0 ? 'text-red-600' : 'text-emerald-700',
    },
  ];
  return (
    <div className="grid shrink-0 gap-3 sm:grid-cols-3">
      {cards.map((c) => (
        <div key={c.label} className="rounded-2xl border bg-card px-5 py-4">
          <p className="text-xs text-muted-foreground">{c.label}</p>
          <p className={cn('text-2xl font-semibold tabular-nums', c.tone)}>
            {formatMoney(c.valor, moneda)}
          </p>
        </div>
      ))}
    </div>
  );
}

function DetalleDialog({
  detalle,
  moneda,
  granularidad,
  vista,
  casa,
  onClose,
}: {
  detalle: { row: EfRow; mes: string | null } | null;
  moneda: string;
  granularidad: EfGranularidad;
  vista: EfVista;
  casa: EfCasa;
  onClose: () => void;
}) {
  const { config: activeCountry } = useActiveCountry();
  const subitemId = detalle?.row.subitemId ?? null;
  const mes = detalle?.mes ?? null;

  const q = useQuery({
    // `vista` y `casa` en la key: el mismo subítem y mes dan importes
    // distintos en pesos que en dólares, y con cada casa.
    queryKey: ['estado-financiero-detalle', activeCountry.code, subitemId, mes, vista, casa],
    queryFn: () => getEstadoFinancieroDetalle({ subitemId, mes: mes!, vista, casa }),
    // The Total column has no single month to drill into — the dialog
    // shows the month breakdown instead of a movement list.
    enabled: !!detalle && mes !== null,
    staleTime: 60_000,
  });

  const porMesOrdenado = useMemo(
    () => Object.entries(detalle?.row.porMes ?? {}),
    [detalle],
  );

  // Columna Total: no hay un mes al que entrar, así que en vez del listado
  // se muestra la apertura período a período.
  if (detalle && mes === null) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="flex max-h-[85vh] w-[min(32rem,95vw)] max-w-none flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle className="pr-8">{`${detalle.row.label} · Total del período`}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto px-6 py-2">
            <table className="w-full text-sm">
              <tbody>
                {porMesOrdenado.map(([m, valor]) => (
                  <tr key={m} className="border-b last:border-b-0">
                    <td className="py-2.5">{tituloColumna(m, granularidad)}</td>
                    <td
                      className={cn(
                        'py-2.5 text-right tabular-nums',
                        valor < 0 ? 'text-red-600' : 'text-emerald-700',
                      )}
                    >
                      {formatMoney(valor, moneda)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <MovimientosDetalleDialog
      abierto={!!detalle}
      titulo={`${detalle?.row.label ?? ''}${mes ? ` · ${tituloColumna(mes, granularidad)}` : ''}`}
      rows={q.data?.rows ?? []}
      moneda={moneda}
      cargando={q.isPending}
      error={q.isError}
      onClose={onClose}
    />
  );
}

/** `2025-12-30` → `30/12/2025`. */
function fechaCortaIso(iso: string | null): string {
  if (!iso) return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

/** `$ 1.102.644.338` — no decimals; the statement reads in millions. */
export function formatMoney(n: number, moneda: string): string {
  const symbol = moneda === 'USD' ? 'US$' : '$';
  return `${symbol} ${Math.round(n).toLocaleString('es-AR')}`;
}
